import {
  Flex,
  CurrencyValue,
  LineChart,
  formatPercentage,
  PercentageValue,
  DisplaySwitch,
  BarChart,
  ChartSkeleton
} from '@blockanalitica/ui';
import { useCallback, useMemo, useState } from 'react';
import { ChartConfig } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { useApi } from '@/api';
import { useTheme } from 'styled-components';

type CollateralAtRiskResponse = {
  drop: number;
  amount: string;
  amount_usd: string;
  cumulative_amount: string;
  cumulative_amount_usd: string;
};

export default function HistoricGraphs({
  marketAddress,
  collateralAddress
}: {
  marketAddress: string;
  collateralAddress: string;
}) {
  const theme = useTheme();
  const [data, error] = useApi<CollateralAtRiskResponse[]>({
    url: `/markets/${marketAddress}/collaterals/${collateralAddress}/at-risk/`
  });

  const [activeDisplayOption, setActiveDisplayOption] = useState('cumulative');
  const displayOptions = useMemo(
    () => [
      { key: 'cumulative', value: 'Cumulative' },
      { key: 'per_drop', value: 'Per Drop' }
    ],
    []
  );

  const valueFormatter = useCallback((value: string | number) => {
    return (
      <CurrencyValue
        value={value}
        variant="dynamic"
        options={{
          currency: 'USD'
        }}
      />
    );
  }, []);

  const subValueFormatter = useCallback((value: string | number) => {
    return (
      <Flex gap="3xs">
        Price drop: <PercentageValue value={-Number(value) / 100} />
      </Flex>
    );
  }, []);

  const config: ChartConfig = useMemo(() => {
    return {
      options: {
        aspectRatio: 3.8,
        scales: {
          x: {
            type: 'linear'
          }
        }
      }
    };
  }, []);

  const xScaleValueFormatter = useCallback((value: string | number) => {
    return formatPercentage(Number(value) / 100);
  }, []);

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }
  return (
    <Flex flexDirection="column" gap="xs" flex={1}>
      <DisplaySwitch
        data={displayOptions}
        activeOption={activeDisplayOption}
        onChange={setActiveDisplayOption}
      />
      {activeDisplayOption === 'per_drop' ? (
        <BarChart
          data={data}
          config={config}
          datasets={[
            {
              xField: 'drop',
              yField: 'amount_usd',
              backgroundColor: theme.colors.chartLine
            }
          ]}
          valueFormatter={valueFormatter}
          subValueFormatter={subValueFormatter}
          xScaleValueFormatter={xScaleValueFormatter}
        />
      ) : (
        <LineChart
          data={data}
          config={config}
          datasets={[
            {
              xField: 'drop',
              yField: 'cumulative_amount_usd'
            }
          ]}
          valueFormatter={valueFormatter}
          subValueFormatter={subValueFormatter}
          xScaleValueFormatter={xScaleValueFormatter}
        />
      )}
    </Flex>
  );
}
